import { oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const KEYWORDS = "spo office 365 m365 graph sites document libraries intranet";

export const sharePoint = defineProduct({
  name: "Microsoft SharePoint",
  tagLine: "Document management and collaboration",
  logo,
  tags: ["productivity", "documents"],
  variants: [
    productVariant({
      identifier: "sharePointUser",
      authLabel: "User OAuth",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://graph\\.microsoft\\.com/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Connect your Microsoft SharePoint account",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in with your Microsoft work or school account. Workflows can read and edit SharePoint documents and list items you have access to. Your organization may require administrator approval.",
            },
            { kind: "oauth" },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () =>
          "https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize",
        tokenEndpoint: () =>
          "https://login.microsoftonline.com/organizations/oauth2/v2.0/token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "managed",
        scopes: () => [
          "offline_access",
          "https://graph.microsoft.com/Sites.ReadWrite.All",
        ],
        pkce: () => true,
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://graph.microsoft.com/v1.0/sites/root",
      },
    }),
    productVariant({
      identifier: "sharePoint",
      authLabel: "App credentials",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://graph\\.microsoft\\.com/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Register an app in Microsoft Entra ID",
          items: [
            {
              kind: "markdown",
              content:
                "In the [Azure portal](https://portal.azure.com) → **Microsoft Entra ID** → **App registrations** → **New registration**. Single tenant is fine; no redirect URI is needed.",
            },
            {
              kind: "markdown",
              content:
                "Under **API permissions**, add **Microsoft Graph** → **Application** permissions for the SharePoint sites you need (e.g., `Sites.Selected`, `Sites.Read.All`, or `Sites.ReadWrite.All`), then click **Grant admin consent**.",
            },
            {
              kind: "markdown",
              content:
                "Under **Certificates & secrets**, create a new client secret and copy the **Value** (not the Secret ID).",
            },
          ],
        },
        {
          title: "Enter your tenant and app credentials",
          items: [
            {
              kind: "input",
              label: "Directory (tenant) ID",
              required: true,
              target: { kind: "configuration", field: "tenantId" },
              helperText: "From the Azure app overview page.",
            },
            {
              kind: "input",
              label: "Application (client) ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: ({ tenantId }) =>
          `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["https://graph.microsoft.com/.default"],
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://graph.microsoft.com/v1.0/sites/root",
      },
    }),
  ],
});
